<template>
  <header
    v-if="userIsAuthenticated && isCubeSelected"
    class="layout__header"
  >
    <a
      @click="onClickBackLink()"
      :class="{ header__back: true, 'header__back--visible': showBackLink }"
      data-cy="nav-back"
    >
      <ArrowLeft />
      <span class="header__back-text">
        {{ t('global.back') }}
      </span>
    </a>
    <router-link :to="{ name: 'home' }" class="header__supplier">
      <img
        :src="assetPath('platform-logo.png')"
        :alt="t('platform.name')"
        class="header__supplier-logo"
      />
    </router-link>
    <button
      :class="menuSlideinToggleClasses"
      @click="appStore.showSlidein('menu', 'toggle')"
      :title="t('global.toggleMenuSlidein')"
      :data-unread-notifications="unreadNotificationsLength"
      role="button"
      data-cy="side-menu-toggle"
    >
      <span class="menu-slidein-toggle__text">
        {{ nickName }}
      </span>
      <img
        :class="cubeIconClasses"
        :src="supplierDataIconUrl"
        :alt="supplierDataName"
      />
    </button>
  </header>
</template>

<script>
  import ArrowLeft from '@/components/icons/ArrowLeft.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { MAIN_ROUTE_NAMES } from '@/constants.ts'
  import { assetPath } from '@/util'
  import { useAppStore } from '@/stores/useAppStore'
  import { storeToRefs } from 'pinia'
  import { useAuthStore } from '@/stores/useAuthStore'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { useAccountStore } from '@/stores/useAccountStore'

  export default {
    name: 'app-header',
    components: {
      ArrowLeft,
    },
    setup() {
      const router = useRouter()
      const { t } = useStrings()
      const appStore = useAppStore()
      const cubeStore = useCubeStore()
      const accountStore = useAccountStore()
      const { userIsAuthenticated } = storeToRefs(useAuthStore())
      const { currentRouteName } = storeToRefs(appStore)

      return {
        appStore,
        cubeStore,
        accountStore,
        router,
        assetPath,
        t,
        userIsAuthenticated,
        isCubeSelected: computed(() => !!cubeStore.name),
        unreadNotificationsLength: computed(() => cubeStore.unreadNotifications.length),
        nickName: computed(() => cubeStore.nickName),
        supplierDataIconUrl: computed(() => cubeStore.supplier.iconUrl),
        supplierDataName: computed(() => cubeStore.supplier.name),
        currentRouteName,
      }
    },
    data() {
      return {
        showBackLink: false,
        numUnreadNotifications: 0,
      }
    },
    mounted() {
      this.updateShowBackLink()
    },
    computed: {
      menuSlideinToggleClasses() {
        let classes = ['menu-slidein-toggle']

        if (this.cubeStore.unreadNotifications.length > 0) {
          classes.push('menu-slidein-toggle--has-notifications')
        }

        return classes.join(' ')
      },
      cubeIconClasses() {
        const type = !this.accountStore.properties.isCommunityless && this.cubeStore.hasProduction //
          ? this.cubeStore.productionType
          : 'consumer'
        let result = [
          'menu-slidein-toggle__icon',
          'menu-slidein-toggle__icon--' + type,
        ]
        return result.join(' ')
      },
    },
    methods: {
      onClickBackLink() {
        if (this.currentRouteName === 'device-detail') {
          this.router.push({ name: 'devices' })
        } else {
          this.router.go(-1)
        }
      },
      updateShowBackLink() {
        this.showBackLink = MAIN_ROUTE_NAMES.indexOf(this.currentRouteName) === -1
      },
    },
    watch: {
      currentRouteName: function () {
        this.updateShowBackLink()
      },
    },
  }
</script>

<style lang="scss">
  .menu-slidein-toggle {
    transition: $transition-base;
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-end;
    align-items: center;
    font-weight: 500;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    color: $grey-darker;
    position: relative;

    @media (min-width: $bp-sm) {
      width: $header-item-width-sm;
    }

    &--has-notifications {
      padding-right: $spacer05;

      @media (min-width: $bp-sm) {
        padding-right: 0;
      }

      &::after {
        @include notification-dot;
        width: 1.5em;
        height: 1.5em;
        content: attr(data-unread-notifications);
        font-size: 11px;
        right: 0;
        top: 0;
        transform: translate(-5%, -30%);
        color: $white;

        @media (min-width: $bp-sm) {
          transform: translate(40%, -30%);
        }
      }
    }

    &__text {
      line-height: 1.1;

      @media (max-width: ($bp-sm - 1px)) {
        max-width: 40vw;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }

    &__icon {
      width: 2.3rem;
      margin-left: $spacer05;
      transition: $transition-base;
      border-radius: 100%;
      border: 5px solid;

      &--consumer {
        border-color: $marker-color-consumer;
      }

      &--pv{
        border-color: $marker-color-pv;
      }

      &--battery {
        border-color: $marker-color-pv;
      }

      &--biogas {
        border-color: $marker-color-biogas;
      }

      &--water {
        border-color: $marker-color-water;
      }

      &--wind {
        border-color: $marker-color-wind;
      }
    }
  }

  .header__supplier {
    display: flex;
    align-items: center;

    @media (max-width: ($bp-sm - 1)) {
      width: 2rem;
      overflow: hidden;
      flex-grow: 1;
    }

    &-logo {
      height: $supplier-logo-height-min;
      width: auto;

      @media (min-width: $bp-md) {
        height: $supplier-logo-height-md;
      }
    }
  }

  .header__back {
    display: none;
    visibility: hidden;
    color: $body-color;
    width: $header-item-width;
    align-items: center;

    @media (min-width: $bp-md) {
      display: flex;
    }

    &--visible {
      display: flex;
      visibility: visible;
    }

    @media (min-width: $bp-sm) {
      width: $header-item-width-sm;
    }

    @include hover-focus-active {
      color: $body-color;
    }

    &-text {
      display: none;

      @media (min-width: $bp-sm) {
        display: inline;
      }
    }

    .icon {
      width: 1em;
      margin-right: $spacer05;
    }
  }
</style>
